import { useMemo } from 'react'
import { memoize } from 'lodash/fp'

const convertTimezone = (utcDate: Date, offsetInSeconds: number) => {
  const localOffsetSeconds = new Date().getTimezoneOffset() * 60
  const offsetMillis = (localOffsetSeconds + offsetInSeconds) * 1000
  return new Date(utcDate.getTime() + offsetMillis)
}

function formatDate(date: Date, options: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

function formatTime(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
  }).format(date)
}

function offsetToGMT(totalSeconds: number) {
  const hours = totalSeconds / 3600
  const sign = hours >= 0 ? '+' : '-'
  return `GMT${sign}${Math.abs(Math.trunc(hours))}`
}

export function toISONoMs(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

export function getTimeString(date?: Date | null) {
  if (!date) return null

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export function combineDateAndTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)
  const combinedDate = new Date(date)
  combinedDate.setHours(hours, minutes, 0, 0)
  return combinedDate
}

export function getTimeUntilEventStart(start: string): string | undefined {
  const startDate = new Date(start)
  const now = new Date()
  const diff = startDate.getTime() - now.getTime()

  if (diff <= 0) return // already started

  const totalMinutes = Math.ceil(diff / (1000 * 60))
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60

  if (days >= 4) return `${days}d`
  if (days >= 1 && hours === 0) return `${days}d`
  if (days >= 1) return `${days}d ${hours}h`
  if (hours >= 1 && minutes === 0) return `${hours}h`
  if (hours >= 1) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

export function getStartOfNextDay() {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  date.setHours(0, 0, 0, 0)
  return date
}

type ParseDatesProps = {
  startDate: Date
  endDate: Date | null
  monthFormat?: 'long' | 'short'
}

const parseDates = memoize(
  ({ startDate, endDate, monthFormat = 'short' }: ParseDatesProps) => {
    const now = new Date()

    const currentYear = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
    }).format(now)

    const startYear = formatDate(startDate, {
      year: 'numeric',
    })
    const startDateFormatted = formatDate(startDate, {
      day: 'numeric',
      month: monthFormat,
      ...(currentYear !== startYear && { year: 'numeric' }),
    })
    const startTimeFormatted = formatTime(startDate)

    if (!endDate) {
      return {
        startDateStr: startDateFormatted,
        startTimeStr: startTimeFormatted,
        endDateStr: undefined,
        endTimeStr: undefined,
      }
    }

    const endYear = formatDate(endDate, {
      year: 'numeric',
    })
    const endDateFormatted = formatDate(endDate, {
      day: 'numeric',
      month: monthFormat,
      ...(currentYear !== endYear && { year: 'numeric' }),
    })
    const endTimeFormatted = formatTime(endDate)

    return {
      startDateStr: startDateFormatted,
      startTimeStr: startTimeFormatted,
      endDateStr:
        startDateFormatted !== endDateFormatted ? endDateFormatted : undefined,
      endTimeStr: endTimeFormatted,
    }
  },
)

export type ParseEventDatesProps = {
  startTime: string
  endTime?: string | null
  timeZoneOffsetSeconds: number
  monthFormat?: 'long' | 'short'
}

export type EventDateNodes = {
  startDateStr: string
  startTimeStr: string
  endDateStr: string | undefined
  endTimeStr: string | undefined
  gmtStr?: string
}

export const parseEventDates = memoize(
  ({
    startTime,
    endTime,
    timeZoneOffsetSeconds,
    monthFormat = 'short',
  }: ParseEventDatesProps): [EventDateNodes, EventDateNodes | undefined] => {
    const localOffsetSeconds = -new Date().getTimezoneOffset() * 60
    const isDifferentTimezone = timeZoneOffsetSeconds !== localOffsetSeconds

    const gmtStr = isDifferentTimezone
      ? offsetToGMT(timeZoneOffsetSeconds)
      : undefined

    const startDate = new Date(startTime)
    const endDate = endTime ? new Date(endTime) : null

    let eventTimezoneDateInfo
    if (isDifferentTimezone) {
      eventTimezoneDateInfo = {
        gmtStr,
        ...parseDates({
          startDate: convertTimezone(startDate, timeZoneOffsetSeconds),
          endDate: endDate && convertTimezone(endDate, timeZoneOffsetSeconds),
          monthFormat,
        }),
      }
    }

    return [
      parseDates({
        startDate,
        endDate,
        monthFormat,
      }),
      eventTimezoneDateInfo,
    ]
  },
)

export const useParseEventDates = (props: ParseEventDatesProps) => {
  return useMemo(() => parseEventDates(props), [props])
}
